'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@/components/Skeleton';
import { env } from '@/env/client.mjs';
import { type EmbedProps } from '@/features/powerbi/PowerBiReact';
import type { Statistic } from '@/payload/payload.types';
import { api } from '@/trpc/react';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import { useIntersectionObserver, useIsClient } from 'usehooks-ts';
const PowerBIEmbed = dynamic(() => import('./PowerBiReact').then(module => module.PowerBIEmbed), {
  ssr: false
});
export const PowerBi = t0 => {
  const $ = _c(24);
  const {
    data,
    isEditing
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      freezeOnceVisible: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    isIntersecting,
    ref
  } = useIntersectionObserver(t1);
  const t2 = data.reportId as string;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      reportId: t2
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== data.reportId || $[4] !== isIntersecting) {
    t4 = {
      retry: 1,
      retryDelay: 3000,
      staleTime: Infinity,
      enabled: t5 => {
        const {
          state
        } = t5;
        if (state.error?.data?.httpStatus === 503) {
          return false;
        }
        return !!data.reportId && isIntersecting && !env.NEXT_PUBLIC_POWER_BI_DISABLED;
      },
      refetchInterval: _temp
    };
    $[3] = data.reportId;
    $[4] = isIntersecting;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: queryData,
    isLoading,
    isFetched,
    error
  } = api.powerBi.get.useQuery(t3, t4);
  const isClient = useIsClient();
  let t5;
  const t6 = queryData?.embedToken;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      filters: {
        visible: false
      },
      pageNavigation: {
        visible: false
      }
    };
    t8 = {
      statusBar: {
        visible: false
      },
      actionBar: {
        visible: false
      }
    };
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      panes: t7,
      bars: t8,
      navContentPaneEnabled: false,
      background: 1,
      layoutType: 0,
      customLayout: {
        displayOption: 0
      }
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== data.reportId || $[10] !== data.url || $[11] !== t6) {
    t10 = {
      type: "report",
      uniqueId: data.reportId,
      id: data.reportId,
      embedUrl: data.url,
      accessToken: t6,
      tokenType: 1,
      pageView: "actualSize",
      settings: t9
    };
    $[9] = data.reportId;
    $[10] = data.url;
    $[11] = t6;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  t5 = t10;
  const embedConfig = t5;
  if (isLoading || !isFetched && !env.NEXT_PUBLIC_POWER_BI_DISABLED) {
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Skeleton isLoading={true} className="h-full w-full absolute top-0 left-0 bg-semantic-surface-faint" />;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== ref) {
      t12 = <div ref={ref} className="h-full w-full relative">{t11}</div>;
      $[14] = ref;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    return t12;
  }
  if (!queryData?.embedToken || !embedConfig.accessToken || error) {
    let t11;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <p className="text-semantic-content-subtle text-[1rem]">Vabandame, graafikut ei ole võimalik hetkel kuvada.<br /> Palun proovi hiljem uuesti.</p>;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] !== ref) {
      t12 = <div ref={ref} className="flex h-full w-full items-center justify-center text-center">{t11}</div>;
      $[17] = ref;
      $[18] = t12;
    } else {
      t12 = $[18];
    }
    return t12;
  }
  if (!isClient) {
    return null;
  }
  const t11 = isEditing ? -1 : 0;
  let t12;
  if ($[19] !== data.titlePlainText || $[20] !== embedConfig || $[21] !== ref || $[22] !== t11) {
    t12 = <PowerBIEmbed title={data.titlePlainText} ref={ref} embedConfig={embedConfig} className="w-full h-full" tabIndex={t11} />;
    $[19] = data.titlePlainText;
    $[20] = embedConfig;
    $[21] = ref;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
};
function _temp(t0) {
  const {
    state: state_0
  } = t0;
  if (state_0.data?.expireInSeconds) {
    return Math.max(state_0.data.expireInSeconds * 1000, 10000);
  }
  return false;
}